/**
 * 营销活动成本计算工具函数
 */

import type { MarketingActivity, MarketingActivityType, VineMarketplace } from '../../../ProfitScenario/types/marketingActivities';
import { calculateVineEnrollmentFee } from '../../../ProfitScenario/constants/vineFees';

interface CostCalculationParams {
    type: MarketingActivityType;
    units: number;
    marketplace: VineMarketplace;
    videoCostPerUnit: number;
    slotFee: number;
    landedCost: number;
    fbaFee: number;
    shippingCost: number;
}

/**
 * 计算营销活动成本
 */
export const calculateActivityCost = (params: CostCalculationParams): MarketingActivity => {
    const { type, units, marketplace, videoCostPerUnit, slotFee, landedCost, fbaFee, shippingCost } = params;
    const id = `${type}-${Date.now()}`;

    if (type === 'vine') {
        const enrollmentFee = calculateVineEnrollmentFee(marketplace, units);
        const productCost = landedCost * units;
        const shippingCostTotal = shippingCost * units;
        const fbaFeeTotal = fbaFee * units;
        const totalCost = enrollmentFee + productCost + shippingCostTotal + fbaFeeTotal;

        return {
            id,
            type,
            units,
            marketplace,
            enrollmentFee,
            productCost,
            shippingCost: shippingCostTotal,
            fbaFee: fbaFeeTotal,
            totalCost,
        };
    } else if (type === 'influencer') {
        const videoCost = videoCostPerUnit * units;
        return {
            id,
            type,
            units,
            videoCost,
            productCost: 0,
            shippingCost: 0,
            fbaFee: 0,
            totalCost: videoCost,
        };
    } else {
        // product_seeding
        const productCost = landedCost * units;
        const shippingCostTotal = shippingCost * units;
        const fbaFeeTotal = fbaFee * units;
        const totalCost = productCost + shippingCostTotal + fbaFeeTotal + slotFee;

        return {
            id,
            type,
            units,
            slotFee,
            productCost,
            shippingCost: shippingCostTotal,
            fbaFee: fbaFeeTotal,
            totalCost,
        };
    }
};

/**
 * 获取活动类型显示文本
 */
export const getActivityTypeLabel = (type: MarketingActivityType): string => {
    switch (type) {
        case 'vine':
            return '🍇 Vine';
        case 'influencer':
            return '🎬 视频';
        case 'product_seeding':
            return '🎁 种草';
        default:
            return type;
    }
};

/**
 * 获取单位标签文本
 */
export const getUnitsLabel = (type: MarketingActivityType): string => {
    switch (type) {
        case 'vine':
            return '注册数量';
        case 'influencer':
            return '视频数量';
        case 'product_seeding':
            return '赠送数量';
        default:
            return '数量';
    }
};

/**
 * 获取最大单位数
 */
export const getMaxUnits = (type: MarketingActivityType): number => {
    return type === 'vine' ? 30 : 999;
};
